import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Check, Crown, Sparkles, Tv, Gift, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function Subscriptions() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState({});
  const [history, setHistory] = useState([]);
  const [checkout, setCheckout] = useState(null); // {plan, mode: "subscribe"|"trial"}

  useEffect(() => {
    api.get("/subscriptions/plans").then((r) => setPlans(r.data));
    api.get("/subscriptions/me").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const cancel = async () => {
    await api.post("/subscriptions/cancel");
    toast.success("Subscription cancelled");
    refresh();
    api.get("/subscriptions/me").then((r) => setHistory(r.data));
  };

  const planFeatures = {
    basic: ["HD streaming", "1 screen at a time", "Watch on phone, tablet, laptop", "Unlimited movies & shows"],
    standard: ["Full HD streaming", "2 screens simultaneously", "Unlimited downloads", "All Live TV channels"],
    premium: ["4K Ultra HD + HDR", "4 screens simultaneously", "Spatial audio", "Early access to originals"],
  };

  const planIcons = { basic: Tv, standard: Sparkles, premium: Crown };
  const trialUsed = !!user?.trial_used;
  const trialActive = user?.subscription_status === "trialing";
  const trialEndsAt = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  return (
    <div className="bg-[#050505] min-h-screen text-white" data-testid="subscriptions-page">
      <Navbar />
      <div className="pt-32 px-4 sm:px-12 max-w-[1400px] mx-auto pb-20">
        <div className="text-center mb-10">
          <span className="text-xs font-bold text-[#E50914] uppercase tracking-[0.3em]">Plans & Billing</span>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tighter mt-3" style={{ fontFamily: "Outfit" }}>
            {user?.subscription_plan ? "Your subscription" : "Pick your plan"}
          </h1>
          {user?.subscription_plan && (
            <p className="text-[#999] mt-3">
              Currently on <span className="text-white font-bold capitalize">{user.subscription_plan}</span>
              {trialActive && <span className="ml-2 px-2 py-0.5 rounded bg-[#E50914]/20 text-[#E50914] text-xs font-bold">FREE TRIAL · {trialDaysLeft}d left</span>}
              <button onClick={cancel} className="text-[#E50914] hover:underline ml-3" data-testid="cancel-sub-btn">Cancel</button>
            </p>
          )}
        </div>

        {/* Free trial banner */}
        {!user?.subscription_plan && !trialUsed && (
          <div className="mb-10 rounded-2xl border border-[#E50914]/40 bg-gradient-to-r from-[#E50914]/15 via-[#E50914]/5 to-transparent p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="trial-banner">
            <div className="w-14 h-14 rounded-2xl bg-[#E50914] flex items-center justify-center flex-shrink-0">
              <Gift size={28} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#E50914] mb-1">Limited offer</div>
              <h3 className="text-2xl font-black" style={{ fontFamily: "Outfit" }}>3 days on us — try Premium free.</h3>
              <p className="text-sm text-[#bbb] mt-1">
                Card required for verification. Cancel any time before day 3 — you won't be charged.
              </p>
            </div>
            <button onClick={() => setCheckout({ plan: "premium", mode: "trial" })} className="btn-primary !py-3 !px-6 flex-shrink-0" data-testid="start-trial-btn">
              <Gift size={16} /> Start free trial
            </button>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-5 mb-14">
          {Object.entries(plans).map(([key, plan]) => {
            const Icon = planIcons[key];
            const current = user?.subscription_plan === key;
            const featured = key === "standard";
            return (
              <div key={key} className={`p-7 rounded-2xl border transition-all hover:-translate-y-1 ${featured ? "border-[#E50914] bg-gradient-to-b from-[#E50914]/15 to-[#0d0d0d]" : "border-white/10 bg-[#0d0d0d]"}`} data-testid={`sub-plan-${key}`}>
                <div className="flex items-center justify-between mb-1">
                  <Icon className={featured ? "text-[#E50914]" : "text-[#888]"} size={28} />
                  {featured && <span className="text-[10px] font-bold text-[#E50914] uppercase tracking-wider">Most Popular</span>}
                  {current && <span className="text-[10px] font-bold text-[#00C853] uppercase tracking-wider">Current</span>}
                </div>
                <h3 className="text-2xl font-bold mt-3" style={{ fontFamily: "Outfit" }}>{plan.label}</h3>
                <div className="mb-4">
                  <span className="text-5xl font-black" style={{ fontFamily: "Outfit" }}>${plan.price}</span>
                  <span className="text-sm text-[#999]"> /month</span>
                </div>
                <div className="text-xs text-[#888] uppercase tracking-wider mb-1">{plan.quality} • {plan.screens} screen{plan.screens > 1 ? "s" : ""}</div>
                <ul className="space-y-2.5 my-5">
                  {planFeatures[key].map((f) => (
                    <li key={f} className="text-sm text-[#ccc] flex items-start gap-2">
                      <Check className="text-[#E50914] flex-shrink-0 mt-0.5" size={16} /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  disabled={current}
                  onClick={() => setCheckout({ plan: key, mode: "subscribe" })}
                  className={`w-full py-3 rounded-md font-semibold transition ${current ? "bg-white/10 text-[#999] cursor-not-allowed" : featured ? "bg-[#E50914] hover:bg-[#F6121D] text-white" : "bg-white/10 hover:bg-white/20 text-white"}`}
                  data-testid={`sub-btn-${key}`}
                >
                  {current ? "Active" : `Subscribe — $${plan.price}/mo`}
                </button>
              </div>
            );
          })}
        </div>

        {/* Billing history */}
        {history.length > 0 && (
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "Outfit" }}>Billing History</h2>
            <div className="bg-[#0d0d0d] border border-white/5 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="text-[#888] text-xs uppercase tracking-wider">
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4">Date</th>
                    <th className="text-left p-4">Plan</th>
                    <th className="text-left p-4">Amount</th>
                    <th className="text-left p-4">Type</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-b border-white/5 last:border-0">
                      <td className="p-4 text-[#ccc]">{new Date(h.startDate).toLocaleDateString()}</td>
                      <td className="p-4 capitalize">{h.plan}</td>
                      <td className="p-4">${h.amount?.toFixed(2)}</td>
                      <td className="p-4 text-[#aaa]">{h.isTrial ? "Trial" : "Subscription"}</td>
                      <td className="p-4">
                        <span className={`text-xs font-bold px-2 py-1 rounded ${h.status === "active" ? "bg-[#00C853]/20 text-[#00C853]" : h.status === "trialing" ? "bg-[#E50914]/20 text-[#E50914]" : "bg-white/10 text-[#999]"}`}>
                          {h.status}
                        </span>
                      </td>
                      <td className="p-4 text-xs text-[#888] font-mono">{h.paypalOrderId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
      <Footer />

      {/* Checkout modal */}
      {checkout && (
        <CheckoutModal
          plan={checkout.plan}
          mode={checkout.mode}
          planMeta={plans[checkout.plan]}
          onClose={() => setCheckout(null)}
          onSuccess={() => {
            setCheckout(null);
            refresh();
            api.get("/subscriptions/me").then((r) => setHistory(r.data));
          }}
        />
      )}
    </div>
  );
}

function CheckoutModal({ plan, mode, planMeta, onClose, onSuccess }) {
  if (!planMeta) return null;
  const isTrial = mode === "trial";

  const createOrder = async () => {
    const { data } = await api.post("/paypal/create-order", { plan });
    return data.id;
  };

  const onApprove = async (data) => {
    try {
      if (isTrial) {
        await api.post("/subscriptions/start-trial", { plan, paypalOrderId: data.orderID });
        toast.success("Free trial activated! Enjoy 3 days of premium streaming.");
      } else {
        await api.post("/subscriptions/subscribe", { plan, paypalOrderId: data.orderID });
        toast.success(`You're now on ${planMeta.label}!`);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment processing failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" data-testid="checkout-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md p-7">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-[#E50914] uppercase tracking-[0.2em] font-bold">{isTrial ? "3-day free trial" : "Subscribe"}</div>
            <h2 className="text-2xl font-black mt-1" style={{ fontFamily: "Outfit" }}>{planMeta.label} — ${planMeta.price}/mo</h2>
          </div>
          <button onClick={onClose} className="text-[#888] hover:text-white" data-testid="close-checkout"><X /></button>
        </div>

        {isTrial && (
          <div className="bg-[#E50914]/10 border border-[#E50914]/30 rounded-lg p-3 mb-4 text-xs text-[#ddd]">
            <strong className="text-[#E50914]">Card required.</strong> PayPal will authorize your card today. You will not be charged until your trial ends in 3 days. Cancel anytime before then to avoid being charged.
          </div>
        )}

        <ul className="text-sm text-[#bbb] space-y-1.5 mb-5">
          <li className="flex gap-2"><Check size={16} className="text-[#00C853] flex-shrink-0 mt-0.5" /> {planMeta.quality} streaming</li>
          <li className="flex gap-2"><Check size={16} className="text-[#00C853] flex-shrink-0 mt-0.5" /> {planMeta.screens} screen{planMeta.screens > 1 ? "s" : ""}</li>
          <li className="flex gap-2"><Check size={16} className="text-[#00C853] flex-shrink-0 mt-0.5" /> Cancel anytime</li>
        </ul>

        {PAYPAL_CLIENT_ID ? (
          <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
            <PayPalButtons
              style={{ layout: "vertical", color: "blue", shape: "rect", label: "paypal" }}
              createOrder={createOrder}
              onApprove={onApprove}
              onError={(e) => { console.error(e); toast.error("PayPal error"); }}
            />
          </PayPalScriptProvider>
        ) : (
          <div className="text-sm text-[#aaa] text-center py-4">PayPal not configured.</div>
        )}

        <div className="text-[10px] text-[#666] text-center mt-4">
          Powered by PayPal Sandbox • Test buyer: <code className="text-[#999]">sb-buyer@personal.example.com</code>
        </div>
      </div>
    </div>
  );
}
